import { useState } from 'react'
import { sendDebugRetrieval, type WatchPart, type PipelineTrace } from '../api/client'
import { Stepper } from './Stepper'
import { JsonViewer } from './JsonViewer'
import { PartCard } from './PartCard'

export function RetrievalLab() {
  const [rawText, setRawText] = useState('')
  const [partType, setPartType] = useState('')
  const [constraints, setConstraints] = useState({
    movement_id: '',
    compatible_movement_ids: '',
    lug_width: '',
    dial_diameter: '',
    min_case_diameter: '',
    max_case_diameter: '',
    required_colors: '',
    required_materials: '',
    required_style_families: '',
    required_features: '',
    excluded_features: '',
  })
  
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [results, setResults] = useState<WatchPart[]>([])
  const [trace, setTrace] = useState<PipelineTrace | null>(null)
  const [activeStepIndex, setActiveStepIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConstraintChange = (key: string, value: string) => {
    setConstraints((prev) => ({ ...prev, [key]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)
    setTrace(null)
    setResults([])

    try {
      // Build typed constraints to match Backend expectations
      const formattedConstraints: Record<string, any> = {}

      if (constraints.movement_id.trim()) {
        formattedConstraints.movement_id = constraints.movement_id.trim()
      }
      if (constraints.compatible_movement_ids.trim()) {
        formattedConstraints.compatible_movement_ids = constraints.compatible_movement_ids
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      }
      if (constraints.lug_width.trim()) {
        formattedConstraints.lug_width = parseFloat(constraints.lug_width)
      }
      if (constraints.dial_diameter.trim()) {
        formattedConstraints.dial_diameter = parseFloat(constraints.dial_diameter)
      }
      if (constraints.min_case_diameter.trim()) {
        formattedConstraints.min_case_diameter = parseFloat(constraints.min_case_diameter)
      }
      if (constraints.max_case_diameter.trim()) {
        formattedConstraints.max_case_diameter = parseFloat(constraints.max_case_diameter)
      }
      if (constraints.required_colors.trim()) {
        formattedConstraints.required_colors = constraints.required_colors
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      }
      if (constraints.required_materials.trim()) {
        formattedConstraints.required_materials = constraints.required_materials
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      }
      if (constraints.required_style_families.trim()) {
        formattedConstraints.required_style_families = constraints.required_style_families
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      }
      if (constraints.required_features.trim()) {
        formattedConstraints.required_features = constraints.required_features
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      }
      if (constraints.excluded_features.trim()) {
        formattedConstraints.excluded_features = constraints.excluded_features
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      }

      const response = await sendDebugRetrieval(
        rawText || undefined,
        partType || undefined,
        formattedConstraints
      )

      setResults(response.results)
      setTrace(response.trace)
      setActiveStepIndex(0)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during retrieval.')
    } finally {
      setIsLoading(false)
    }
  }

  const steps = trace?.steps.map((step, idx) => ({
    id: idx,
    name: step.name,
    durationMs: step.duration_ms,
    status: step.status,
    itemCount: step.item_count,
  })) || []

  const activeStep = trace?.steps[activeStepIndex]

  return (
    <div className="debug-page retrieval-lab">
      <header className="debug-header">
        <h1>Retrieval Laboratory</h1>
        <p className="subtitle">
          Construct custom search briefs, trigger query parsing, and evaluate the full multi-stage vector and semantic search pipeline.
        </p>
      </header>

      <form className="query-builder-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group flex-2">
            <label htmlFor="rawText">Natural Language Input</label>
            <input
              id="rawText"
              type="text"
              placeholder="e.g., green sunburst NH35 dial with lumes"
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
            />
          </div>
          <div className="form-group flex-1">
            <label htmlFor="partType">Target Part Type</label>
            <select
              id="partType"
              value={partType}
              onChange={(e) => setPartType(e.target.value)}
            >
              <option value="">Any Type</option>
              <option value="case">Case</option>
              <option value="movement">Movement</option>
              <option value="dial">Dial</option>
              <option value="hands">Hands</option>
              <option value="strap">Strap</option>
            </select>
          </div>
        </div>

        <div className="advanced-toggle-row">
          <button
            type="button"
            className="btn-text-link"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            {showAdvanced ? 'Hide Advanced Constraints ▴' : 'Show Advanced Constraints ▾'}
          </button>
        </div>

        {showAdvanced && (
          <div className="advanced-constraints grid-3">
            <div className="form-group">
              <label>Specific Movement ID</label>
              <input
                type="text"
                placeholder="NH35"
                value={constraints.movement_id}
                onChange={(e) => handleConstraintChange('movement_id', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Compatible Movement IDs (comma-separated)</label>
              <input
                type="text"
                placeholder="NH35, NH36, NH38"
                value={constraints.compatible_movement_ids}
                onChange={(e) => handleConstraintChange('compatible_movement_ids', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Lug Width (mm)</label>
              <input
                type="number"
                step="0.1"
                placeholder="20"
                value={constraints.lug_width}
                onChange={(e) => handleConstraintChange('lug_width', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Dial Diameter (mm)</label>
              <input
                type="number"
                step="0.1"
                placeholder="28.5"
                value={constraints.dial_diameter}
                onChange={(e) => handleConstraintChange('dial_diameter', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Min Case Diameter (mm)</label>
              <input
                type="number"
                step="0.1"
                placeholder="36"
                value={constraints.min_case_diameter}
                onChange={(e) => handleConstraintChange('min_case_diameter', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Max Case Diameter (mm)</label>
              <input
                type="number"
                step="0.1"
                placeholder="42"
                value={constraints.max_case_diameter}
                onChange={(e) => handleConstraintChange('max_case_diameter', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Required Colors</label>
              <input
                type="text"
                placeholder="blue, green"
                value={constraints.required_colors}
                onChange={(e) => handleConstraintChange('required_colors', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Required Materials</label>
              <input
                type="text"
                placeholder="steel, leather"
                value={constraints.required_materials}
                onChange={(e) => handleConstraintChange('required_materials', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Required Styles</label>
              <input
                type="text"
                placeholder="diver, dress"
                value={constraints.required_style_families}
                onChange={(e) => handleConstraintChange('required_style_families', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Required Features</label>
              <input
                type="text"
                placeholder="lume, date"
                value={constraints.required_features}
                onChange={(e) => handleConstraintChange('required_features', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Excluded Features</label>
              <input
                type="text"
                placeholder="date"
                value={constraints.excluded_features}
                onChange={(e) => handleConstraintChange('excluded_features', e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={isLoading}>
            {isLoading ? 'Executing Search Pipeline...' : 'Run Pipeline Tracer'}
          </button>
        </div>
      </form>

      {isLoading && (
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
          <p>Processing text embedding and scanning databases. Step-by-step trace is generating...</p>
        </div>
      )}

      {error && (
        <div className="error-banner">
          <h3>Retrieval Error</h3>
          <p>{error}</p>
        </div>
      )}

      {trace && (
        <section className="retrieval-trace-section">
          <div className="trace-summary-bar">
            <span>Overall Latency: <strong>{trace.total_duration_ms}ms</strong></span>
            <span>Total Stages: <strong>{trace.steps.length}</strong></span>
            <span>Retrieved Candidates: <strong>{trace.results_count} parts</strong></span>
          </div>

          <Stepper
            steps={steps}
            activeStepIndex={activeStepIndex}
            onStepChange={setActiveStepIndex}
          >
            {activeStep && (
              <div className="step-trace-pane">
                <div className="step-pane-header">
                  <h2>{activeStep.name}</h2>
                  <div className="step-pane-badges">
                    <span className={`badge-status ${activeStep.status}`}>
                      {activeStep.status.toUpperCase()}
                    </span>
                    <span className="badge-time">{activeStep.duration_ms}ms</span>
                  </div>
                </div>

                {activeStep.error_message && (
                  <div className="step-error">
                    <strong>Error: </strong> {activeStep.error_message}
                  </div>
                )}

                <div className="step-content-grid">
                  {/* Step specific human-friendly visualizations */}
                  {activeStep.name === 'Search Brief Parsing' && (
                    <div className="step-visualizer card">
                      <h3>Extracted Intent Brief</h3>
                      <div className="brief-box">
                        <p><strong>Raw Text:</strong> "{(activeStep.input_data as any).raw_text || ''}"</p>
                        <div className="parsed-brief-section">
                          <h4>Global Search Filters</h4>
                          <ul>
                            <li><strong>Colors:</strong> {((activeStep.output_data as any).global_intent?.colors || []).join(', ') || 'none'}</li>
                            <li><strong>Styles:</strong> {((activeStep.output_data as any).global_intent?.style_families || []).join(', ') || 'none'}</li>
                            <li><strong>Materials:</strong> {((activeStep.output_data as any).global_intent?.materials || []).join(', ') || 'none'}</li>
                            <li><strong>Finishes:</strong> {((activeStep.output_data as any).global_intent?.finishes || []).join(', ') || 'none'}</li>
                            <li><strong>Features:</strong> {((activeStep.output_data as any).global_intent?.features || []).join(', ') || 'none'}</li>
                            <li><strong>Movement IDs:</strong> {((activeStep.output_data as any).global_intent?.movement_ids || []).join(', ') || 'none'}</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeStep.name === 'Strict Compatibility Pre-Filtering' && (
                    <div className="step-visualizer card">
                      <h3>Hard Compatibility Mismatches</h3>
                      <p className="explanation">
                        Below are the catalog items that were matched semantically, but were rejected due to hard mechanical constraints (e.g. dial size, movement incompatibility, lug width).
                      </p>
                      <div className="rejections-list">
                        {Object.keys(activeStep.output_data.rejection_reasons || {}).length === 0 ? (
                          <div className="no-items">No parts were filtered out by compatibility in this turn.</div>
                        ) : (
                          <table className="debug-table">
                            <thead>
                              <tr>
                                <th>Part ID</th>
                                <th>Reason for Rejection</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(activeStep.output_data.rejection_reasons || {}).map(([id, reason]) => (
                                <tr key={id}>
                                  <td><code>{id}</code></td>
                                  <td className="rejection-text">{reason as string}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  )}

                  {activeStep.name === 'Multi-Signal Scoring & Reranking' && (
                    <div className="step-visualizer card">
                      <h3>Multi-Signal Score Breakdowns</h3>
                      <p className="explanation">
                        Calculated by weight-scoring similarity, term overlap, constraint compliance, mechanical compatibility, data completeness, and commercial factors.
                      </p>
                      <table className="debug-table math-table">
                        <thead>
                          <tr>
                            <th>Part ID</th>
                            <th title="Vector Match similarity score">Vector</th>
                            <th title="Exact text term overlaps">Terms</th>
                            <th title="Compliance with numeric constraints">Constraints</th>
                            <th title="Anchor compatibility weight">Compat</th>
                            <th title="Price, budget, availability and trustworthiness">Comm</th>
                            <th title="Completeness of photos and description specs">Quality</th>
                            <th>Total Weighted</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(activeStep.output_data.score_breakdowns || {}).map(([id, breakdown]: [string, any]) => (
                            <tr key={id}>
                              <td><code>{id}</code></td>
                              <td>{breakdown.retrieval?.toFixed(3)}</td>
                              <td>{breakdown.exact_term?.toFixed(3)}</td>
                              <td>{breakdown.constraint_fit?.toFixed(3)}</td>
                              <td>{breakdown.compatibility?.toFixed(3)}</td>
                              <td>{breakdown.commercial?.toFixed(3)}</td>
                              <td>{breakdown.data_quality?.toFixed(3)}</td>
                              <td className="highlight"><strong>{breakdown.total?.toFixed(3)}</strong></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="step-input-block">
                    <JsonViewer data={activeStep.input_data} title="Step Inputs" />
                  </div>
                  <div className="step-output-block">
                    <JsonViewer data={activeStep.output_data} title="Step Outputs" />
                  </div>
                </div>
              </div>
            )}
          </Stepper>
        </section>
      )}

      {results.length > 0 && (
        <section className="results-preview-section">
          <h2>Final Ranked Shortlist</h2>
          <p className="subtitle">These items are returned to the caller, sorted by their multi-signal score.</p>
          <div className="parts-grid-preview">
            {results.map((part) => {
              const score = (part.metadata._rerank_score as number) || 0;
              const reason = (part.metadata._rerank_breakdown as any)?.total
                ? `Multi-signal rank score is ${score.toFixed(3)}.`
                : undefined;
              const mockDecision = reason
                ? {
                    part_type: part.type,
                    selected_part: part,
                    candidate_count: results.length,
                    reason: reason,
                    rejected_parts: [],
                  }
                : undefined;
              return (
                <div key={part.id} className="part-card-debug-wrapper">
                  <div className="part-score-tag">
                    Score: <strong>{score.toFixed(3)}</strong>
                  </div>
                  <PartCard partType={part.type} part={part} decision={mockDecision} />
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  )
}
